// client/src/lib/validation.ts — shared Zod fragments (04 §5.4, NORMATIVE pattern) · AUTH-004.
//
// Zod v4 API throughout (`z.email()`, `error:` — never v3's `message`). Error values are
// `validation.*` KEYS of romanian.json, resolved at render by `useValidationMessage()`
// (components/forms/FormField.tsx), so the schemas stay language-free: Appendix B adds a
// language with zero schema changes.
//
// `ValidationKey` is the closed list of keys a schema may emit. It exists because the i18n
// gate refuses a computed `t(error)` (04 §9.6.3 — only `enums.*` may be dynamic), so FormField
// resolves errors through a literal table; a key not in that table would render as its own
// path, and the type is what makes forgetting one a compile error rather than a screen bug.
// See DEVIATIONS D-183.

import { z } from 'zod';

export const VALIDATION_KEYS = [
  'validation.required',
  'validation.email',
  'validation.passwordMin',
  'validation.passwordMatch',
  'validation.code',
  // PROP-004 — the portfolio forms are the first to mirror column CHECKs rather than only
  // „is it filled in": the `varchar(n)` widths, the `numeric` scales and the two range
  // constraints of 03 §7.2/§7.5. Each is a closed rule with one Romanian sentence, so each is
  // a key here rather than a sentence in a schema (04 §5.4).
  'validation.tooLong',
  'validation.positiveNumber',
  'validation.buildYear',
  'validation.percent',
  'validation.date',
  'validation.currency',
  // TEN-004 — the leasing party's two identification rules. `validation.cui` was already in
  // romanian.json (04 §9.6.2 seeds it) and had no consumer until the CUI field arrived; both
  // are closed format rules with one Romanian sentence each, so both are keys here rather than
  // sentences in a schema (04 §5.4). See DEVIATIONS D-379.
  'validation.cui',
  'validation.cnp',
  // CON-005 — the contract wizard is the first form whose rules are mostly CROSS-FIELD: the
  // period, the FX rule against the currency, the clause that needs its month, the one primary
  // party, the act adițional inside the contract's period. Each mirrors one 422 of
  // `ContractErrors` (CON-001) and each is a closed rule with one Romanian sentence, so each is
  // a key here rather than a sentence in a schema (04 §5.4). `ratePercent` exists beside
  // `percent` because `ContractInputValidator` bounds the indexation and penalty percentages at
  // `numeric(6,3)`'s 0–999.999, not at 0–100. See DEVIATIONS D-384.
  'validation.dayOfMonth',
  'validation.month',
  'validation.endAfterStart',
  'validation.onePrimaryTenant',
  'validation.fxRuleOnRon',
  'validation.ratePercent',
  'validation.withinContractPeriod',
  // EXP-005 — three rules the expenses forms mirror off the server. `paidDateOnlyWhenPaid` is
  // `ExpenseErrors.PaidDateNotAllowed`'s 422 said before the round trip (the OCR confirmation
  // raises it; SCR-EXP-002 empties the hidden date instead, D-3120), and the other two are
  // `InvoiceInputValidator`'s two cross-field 400s — arithmetic an invoice simply IS, which is
  // why D-804 makes them field errors rather than rule codes. Each is one closed rule with one
  // Romanian sentence, so each is a key here rather than a sentence in a schema (04 §5.4).
  'validation.paidDateOnlyWhenPaid',
  // EXP-005 phase 2 — the recurring-rule builder and the catalogue form. `categoryCode`
  // mirrors `ExpenseCategoryInputValidator`'s alphabet (a 400, a property of the string);
  // `endNotBeforeStart` mirrors `recurring-expense-end-before-start`, and is a key of its own
  // because `endAfterStart`'s sentence is the CONTRACT's rule about determinate leases.
  'validation.categoryCode',
  'validation.endNotBeforeStart',
  'validation.dueAfterIssue',
  'validation.vatWithinTotal',
  // DOC-004 — the vault's one cross-field rule, `DocumentVaultErrors.ExpirationBeforeIssue`
  // said before the round trip. A key of its own rather than `dueAfterIssue`'s: that sentence
  // is an INVOICE's („data scadentei"), and a certificate has an expiration rather than a due
  // date. The Romanian is the server's, byte for byte, so the field error and the 422 that
  // would follow it read identically.
  'validation.expirationNotBeforeIssue',

  // OAT-003 — the tenancy card's two cross-field rules, each mirroring a CHECK of 03 §16.2 and
  // carrying `OwnerTenancyErrors`' own Romanian. Both are keys of their own rather than reuses:
  // `endNotBeforeStart` admits an end EQUAL to the start and `ck_owner_tenancies_dates` does
  // not, and there is no existing key for „a garanție is given whole or not at all".
  'validation.tenancyEndAfterStart',
  'validation.depositBothOrNeither',

  // ALR-004 — SCR-SET-005's two format rules, each mirroring a column of 03 §15.2.
  // `alertOffsets` is `alert_offsets_days`, an `int[]` the screen edits as text („60, 30, 14");
  // `quietHours` is `email_quiet_hours`, a `varchar(11)` the server reads as „HH:MM-HH:MM"
  // (`NotificationRuleInputValidator`). Both are closed format rules with one Romanian sentence
  // each, so both are keys here rather than sentences in a schema (04 §5.4).
  'validation.alertOffsets',
  'validation.quietHours',

  // D-2121 — the contract wizard's rule that the lease does not begin before it was signed,
  // `ContractErrors.StartBeforeSigning` said before the round trip. A key of its own rather
  // than `endAfterStart`'s: that sentence is about the END of a determinate lease, and this one
  // is about its start against the signing date. The same day passes.
  'validation.startNotBeforeSigned',

  // D-2710 — the login box takes an address or the user name of an account a landlord created
  // for a tenant without one. A login with no `@` is a user name, and this is
  // `ManagedAccountPolicy.UserNamePattern`'s rule said before the round trip — a closed format
  // rule with one Romanian sentence, so a key here rather than a sentence in a schema.
  'validation.userName',

  // D-3110 — the CNP's control digit, `Cnp.IsWellFormed` said before the round trip. A key of
  // its own beside `validation.cnp`: thirteen digits that do not check out are a different
  // mistake from twelve, and the sentence is the server's „CNP-ul nu este valid." byte for byte.
  'validation.cnpInvalid',
] as const;

export type ValidationKey = (typeof VALIDATION_KEYS)[number];

const KNOWN: ReadonlySet<string> = new Set(VALIDATION_KEYS);

export function isValidationKey(value: string): value is ValidationKey {
  return KNOWN.has(value);
}

/** Keeps a literal honest at the point of use — `key('validation.typo')` does not compile. */
const key = (value: ValidationKey): ValidationKey => value;

export const zRequired = z.string().trim().min(1, { error: key('validation.required') });

export const zEmail = z
  .string()
  .trim()
  .min(1, { error: key('validation.required') })
  .pipe(z.email({ error: key('validation.email') }));

/** 05 AUTH-001: "at least 12 characters" — the server's `MinPasswordLength`, mirrored. */
export const PASSWORD_MIN_LENGTH = 12;

export const zPassword = z
  .string()
  .min(1, { error: key('validation.required') })
  .min(PASSWORD_MIN_LENGTH, { error: key('validation.passwordMin') })
  .max(256, { error: key('validation.passwordMin') });

/** A six-digit TOTP or e-mail code; spaces a user types between groups are removed. */
export const zSixDigitCode = z
  .string()
  .transform((value) => value.replace(/\s+/g, ''))
  .pipe(z.string().regex(/^\d{6}$/, { error: key('validation.code') }));

/**
 * Two password fields that must agree. Applied with `.refine` so the error lands on the
 * confirmation field, which is the one the user has to fix.
 */
export function passwordsMatch<T extends { password: string; confirmPassword: string }>(values: T): boolean {
  return values.password === values.confirmPassword;
}

export const PASSWORD_MATCH_ISSUE: { error: ValidationKey; path: string[] } = {
  error: key('validation.passwordMatch'),
  path: ['confirmPassword'],
};
